// src/pages/qc/PageTds.jsx
import { useState, useEffect, useMemo } from "react";
import { fetchTdsList, fetchTdsHistory, fetchFgSpec, saveTds, downloadPdf } from "../../apiService";
import { useToast } from "../../components/Toast";

const PRODUCT_NAME    = "STRONG BOND WATER BASED ADHESIVES";
const PACKING_OPTIONS = ["20 Kg pails", "200/220 Kg drums", "1000 Kg IBC"];
const DEFAULT_SAFETY  = "Non hazardous material. It is harmful if ingested. Seek medical help.";
const DEFAULT_NOTE    = "This adhesive can be used for packaging. The ingredients used are FDA approved for indirect food contact. Low VoC, Eco-friendly adhesive.";
const DEFAULT_STORAGE = "Up to 6 months in closed container. Keep in cool and dry place.";

function openTdsPdf(id, code, version) {
  downloadPdf(
    `/api/qc/tds/${id}/pdf`,
    `${(code ?? "TDS").replace(/[^A-Za-z0-9_-]/g, "_")}_v${version}.pdf`
  );
}

const EMPTY = {
  fgId: "", productName: PRODUCT_NAME, productCode: "",
  description: "", application: "",
  packingArr: [],
  safety: DEFAULT_SAFETY, storage: DEFAULT_STORAGE, note: DEFAULT_NOTE,
  properties: [{ parameter: "", value: "", standard: "" }],
};

// ── Build properties rows from fg_spec data ──────────────────────────────────
function buildPropsFromSpec(spec) {
  if (!spec) return [{ parameter: "", value: "", standard: "" }];
  const rows = [];
  if (spec.appearance)
    rows.push({ parameter: "Appearance", value: spec.appearance, standard: "" });
  if (spec.solidContentMin != null)
    rows.push({
      parameter: "Solid Content (%)",
      value: `${spec.solidContentMin} – ${spec.solidContentMax}`,
      standard: spec.testMethodSolidPct ?? "",
    });
  if (spec.brookfieldMin != null)
    rows.push({
      parameter: "Viscosity (Brookfield) cP",
      value: `${spec.brookfieldMin} – ${spec.brookfieldMax} @ ${spec.brookfieldRpm} rpm, S${spec.brookfieldSpindle}${spec.brookfieldTempC ? ` @ ${spec.brookfieldTempC}°C` : ""}`,
      standard: spec.testMethodViscosity ?? "",
    });
  if (spec.b4CupMin != null)
    rows.push({
      parameter: "Viscosity (B4 Cup) sec",
      value: `${spec.b4CupMin} – ${spec.b4CupMax}`,
      standard: spec.testMethodB4Cup ?? "",
    });
  if (spec.phMin != null)
    rows.push({
      parameter: "pH",
      value: `${spec.phMin} – ${spec.phMax}`,
      standard: spec.testMethodPh ?? "",
    });
  return rows.length ? rows : [{ parameter: "", value: "", standard: "" }];
}

// ── Property row editor ──────────────────────────────────────────────────────
function PropsEditor({ rows, onChange }) {
  function update(i, field, val) {
    onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  }
  function addRow()     { onChange([...rows, { parameter: "", value: "", standard: "" }]); }
  function removeRow(i) { onChange(rows.filter((_, idx) => idx !== i)); }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 6, marginBottom: 6 }}>
        <span className="form-label" style={{ minWidth: 0 }}>Parameter</span>
        <span className="form-label" style={{ minWidth: 0 }}>Value</span>
        <span className="form-label" style={{ minWidth: 0 }}>ASTM Standard</span>
        <span />
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 6, marginBottom: 6 }}>
          <input className="form-input" style={{ width: "100%" }} value={r.parameter ?? ""}
            onChange={e => update(i, "parameter", e.target.value)}
            placeholder="e.g. Viscosity (cps)" />
          <input className="form-input" style={{ width: "100%" }} value={r.value ?? ""}
            onChange={e => update(i, "value", e.target.value)}
            placeholder="e.g. 3800 – 4800" />
          <input className="form-input" style={{ width: "100%" }} value={r.standard ?? ""}
            onChange={e => update(i, "standard", e.target.value)}
            placeholder="e.g. ASTM E3116" />
          <button className="btn" style={{ padding: "0 10px", height: 34, fontSize: 13 }}
            type="button" onClick={() => removeRow(i)}>✕</button>
        </div>
      ))}
      <button className="btn" type="button" style={{ fontSize: 12 }} onClick={addRow}>+ Add row</button>
    </div>
  );
}

// ── Packing picker ───────────────────────────────────────────────────────────
function PackingPicker({ value, onChange }) {
  function toggle(opt) {
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  }
  return (
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", paddingTop: 4 }}>
      {PACKING_OPTIONS.map(opt => (
        <label key={opt} style={{ display: "flex", alignItems: "center", gap: 7,
          fontSize: 13, cursor: "pointer", color: "var(--color-text)" }}>
          <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} />
          {opt}
        </label>
      ))}
    </div>
  );
}

// ── TDS editor modal ─────────────────────────────────────────────────────────
function TdsModal({ fgs, initial, onSave, onClose }) {
  const showToast = useToast();
  const [form, setForm] = useState(initial ?? EMPTY);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  async function handleFgChange(fgId) {
    if (!fgId) { setForm(f => ({ ...f, fgId: "" })); return; }
    const fg = fgs.find(f => f.id === fgId);
    let specProps = null;
    try {
      const spec = await fetchFgSpec(fgId);
      specProps = buildPropsFromSpec(spec);
    } catch {
      specProps = [{ parameter: "", value: "", standard: "" }];
    }
    setForm(f => ({
      ...f,
      fgId,
      productName: PRODUCT_NAME,
      productCode: fg ? fg.id : fgId,
      properties: specProps,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const saved = await saveTds({
        ...form,
        packing: form.packingArr.join(", "),
        properties: form.properties.filter(r => r.parameter.trim()),
      });
      showToast("TDS saved");
      onSave(saved);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  const sectionHead = (label) => (
    <>
      <div className="form-sep" />
      <div style={{ fontSize: 11, fontWeight: 700, color: "#888",
        marginBottom: 10, textTransform: "uppercase", letterSpacing: ".05em" }}>
        {label}
      </div>
    </>
  );

  return (
    <div className="modal" style={{ width: 860 }}>
      <div className="modal-header">
        <h3 className="modal-title">{initial ? "Edit TDS (new version)" : "New Technical Data Sheet"}</h3>
        <button className="btn" type="button" onClick={onClose}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Product identity */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label className="form-label">Finished Good *</label>
            <select className="form-input" style={{ width: "100%" }} value={form.fgId}
              onChange={e => handleFgChange(e.target.value)} required>
              <option value="">— select —</option>
              {fgs.map(f => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Product Name</label>
            <input className="form-input" style={{ width: "100%", background: "var(--color-bg)", color: "var(--color-text-secondary)" }}
              value={PRODUCT_NAME} readOnly />
          </div>
          <div>
            <label className="form-label">Product Code *</label>
            <input className="form-input" style={{ width: "100%" }} value={form.productCode}
              onChange={e => setForm(f => ({ ...f, productCode: e.target.value }))}
              required placeholder="e.g. SBSP 115" />
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={3} style={{ width: "100%", resize: "vertical" }}
            value={form.description}
            placeholder="Brief product description, composition, suitability…"
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        {/* Typical Properties */}
        {sectionHead("Typical Properties")}
        {form.fgId && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>
            Auto-populated from Water Based Specifications — editable
          </div>
        )}
        <PropsEditor rows={form.properties}
          onChange={rows => setForm(f => ({ ...f, properties: rows }))} />

        {/* Application */}
        {sectionHead("Application")}
        <textarea className="form-input" rows={3} style={{ width: "100%", resize: "vertical" }}
          value={form.application}
          placeholder="Where and how the product is used…"
          onChange={e => setForm(f => ({ ...f, application: e.target.value }))} />

        {/* Packing */}
        {sectionHead("Packing")}
        <PackingPicker value={form.packingArr}
          onChange={arr => setForm(f => ({ ...f, packingArr: arr }))} />

        {/* Safety */}
        {sectionHead("Safety Precautions")}
        <textarea className="form-input" rows={2} style={{ width: "100%", resize: "vertical" }}
          value={form.safety}
          onChange={e => setForm(f => ({ ...f, safety: e.target.value }))} />

        {/* Storage */}
        {sectionHead("Storage")}
        <textarea className="form-input" rows={2} style={{ width: "100%", resize: "vertical" }}
          value={form.storage}
          onChange={e => setForm(f => ({ ...f, storage: e.target.value }))} />

        {/* Note */}
        {sectionHead("Note")}
        <textarea className="form-input" rows={2} style={{ width: "100%", resize: "vertical" }}
          value={form.note}
          onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />

        {err && <div className="alert-warn" style={{ marginTop: 12 }}>{err}</div>}

        <div className="form-sep" />
        <div className="form-footer">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save as new version"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Version history panel ────────────────────────────────────────────────────
function HistoryPanel({ fgId, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTdsHistory(fgId)
      .then(setHistory)
      .finally(() => setLoading(false));
  }, [fgId]);

  return (
    <div className="modal" style={{ width: 560 }}>
      <div className="modal-header">
        <h3 className="modal-title">Version History</h3>
        <button className="btn" type="button" onClick={onClose}>✕</button>
      </div>
      {loading && <p style={{ color: "var(--color-text-secondary)" }}>Loading…</p>}
      {!loading && history.map(t => (
        <div key={t.id} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 8,
          marginBottom: 8, background: t.isCurrent ? "var(--color-violet-tint)" : "var(--color-bg)",
        }}>
          <div>
            <span style={{ fontWeight: 700, color: "var(--color-violet)", marginRight: 8 }}>v{t.version}</span>
            {t.isCurrent && (
              <span className="badge badge-blue" style={{ marginRight: 8 }}>Current</span>
            )}
            <span style={{ fontSize: 13 }}>{t.productCode}</span>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              Saved {new Date(t.createdAt).toLocaleDateString("en-GB",
                { day: "numeric", month: "short", year: "numeric" })}
              {" "}&mdash; by {t.createdBy}
            </div>
          </div>
          <button className="btn" style={{ fontSize: 12 }} type="button"
            onClick={() => openTdsPdf(t.id, t.productCode, t.version)}>
            ↓ PDF
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export function PageTds({ myPerms, fgs }) {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [modal,   setModal]   = useState(null);

  const canEdit = myPerms?.includes("QC_EDIT");

  useEffect(() => {
    fetchTdsList()
      .then(setList)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter(t =>
      t.productName.toLowerCase().includes(q) ||
      t.productCode.toLowerCase().includes(q)
    );
  }, [list, search]);

  function onSaved(tds) {
    setList(prev => {
      const without = prev.filter(t => t.fgId !== tds.fgId);
      return [...without, tds].sort((a, b) => a.productCode.localeCompare(b.productCode));
    });
    setModal(null);
  }

  function buildEditForm(tds) {
    let props = [{ parameter: "", value: "", standard: "" }];
    try { if (tds.properties) props = JSON.parse(tds.properties); } catch {}
    const packingStr = tds.packing ?? "";
    const packingArr = PACKING_OPTIONS.filter(opt => packingStr.includes(opt));
    return {
      fgId:       tds.fgId,
      productName: PRODUCT_NAME,
      productCode: tds.productCode,
      description: tds.description ?? "",
      application: tds.application ?? "",
      packingArr,
      safety:  tds.safety  || DEFAULT_SAFETY,
      storage: tds.storage || DEFAULT_STORAGE,
      note:    tds.note    || DEFAULT_NOTE,
      properties: props.length ? props : [{ parameter: "", value: "", standard: "" }],
    };
  }

  const wbFgs = useMemo(() =>
    (fgs ?? []).filter(f => !f.id.toLowerCase().startsWith("hm")),
    [fgs]);

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0 }}>Technical Data Sheets</h2>
          <p style={{ margin: "4px 0 0", color: "var(--color-text-secondary)", fontSize: 14 }}>
            Water Based Adhesives &mdash; version-controlled TDS library
          </p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setModal({ type: "create" })}>
            + New TDS
          </button>
        )}
      </div>

      <input className="form-input" style={{ width: 320, marginBottom: 18 }}
        placeholder="Search by product name or code…"
        value={search} onChange={e => setSearch(e.target.value)} />

      {loading && <p style={{ color: "var(--color-text-secondary)" }}>Loading…</p>}
      {error   && <div className="alert-warn">{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="alert-info">
          No TDS records yet.{canEdit && ' Click "+ New TDS" to create the first one.'}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Code</th>
                <th>Product Name</th>
                <th>Version</th>
                <th>Issue Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.productCode}</td>
                  <td>{t.productName}</td>
                  <td>
                    <span className="badge badge-blue">v{t.version}</span>
                  </td>
                  <td style={{ color: "var(--color-text-secondary)" }}>
                    {new Date(t.createdAt).toLocaleDateString("en-GB",
                      { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn" style={{ fontSize: 12 }} type="button"
                        onClick={() => openTdsPdf(t.id, t.productCode, t.version)}>
                        PDF
                      </button>
                      {canEdit && (
                        <button className="btn" style={{ fontSize: 12 }} type="button"
                          onClick={() => setModal({ type: "edit", data: buildEditForm(t) })}>
                          Edit
                        </button>
                      )}
                      <button className="btn" style={{ fontSize: 12 }} type="button"
                        onClick={() => setModal({ type: "history", data: t.fgId })}>
                        History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          {(modal.type === "create" || modal.type === "edit") && (
            <TdsModal
              fgs={wbFgs}
              initial={modal.type === "edit" ? modal.data : null}
              onSave={onSaved}
              onClose={() => setModal(null)} />
          )}
          {modal.type === "history" && (
            <HistoryPanel fgId={modal.data} onClose={() => setModal(null)} />
          )}
        </div>
      )}
    </div>
  );
}
